import { motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  CalendarDays,
  Check,
  CircleDollarSign,
  Plug2,
  Rocket,
  Settings,
  TestTube2,
  UserRound,
} from "lucide-react";
import IntegrationsPreviewMock from "./IntegrationsPreviewMock";
import LabPharmacyPreviewMock from "./LabPharmacyPreviewMock";
import { primaryCtaClass } from "../utils/ctaClasses";
import { iconBoxClass, iconBoxShape } from "../utils/iconBoxClasses";
import { slowFadeUp } from "../utils/animations";

const productPreviewFrameBaseClass =
  "relative w-full overflow-hidden rounded-2xl border border-slate-200 shadow-inner shadow-slate-900/5 aspect-[16/9]";

function previewImageClassName(objectFit) {
  const base = "block h-full w-full min-h-0 object-center";
  if (objectFit === "cover") {
    return `${base} object-cover`;
  }
  return `${base} object-contain p-1 md:p-1.5`;
}

function previewFrameClassName(item) {
  if (item.previewKind) {
    return `${productPreviewFrameBaseClass} bg-gradient-to-br from-white via-slate-50/60 to-slate-100/70`;
  }
  return productPreviewFrameBaseClass;
}

function ProductFeatureCard({ item, renderProductPreview }) {
  const CardIcon = item.Icon;
  const isBottomCard = item.number === "05" || item.number === "06";

  return (
    <article
      className={`relative flex h-full min-h-0 flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-white via-slate-50/60 to-slate-100/70 p-5 shadow-md shadow-slate-900/5 md:flex-row md:items-stretch md:gap-6 md:p-6 ${
        isBottomCard ? "md:py-7" : ""
      }`}
    >
      <div
        className={`absolute left-0 top-10 bottom-10 w-1 rounded-full ${item.styles.accentBar} opacity-90 shadow-sm`}
        aria-hidden
      />
      <div
        className={`flex flex-1 min-h-0 flex-col pl-3 md:grid md:grid-cols-[auto,minmax(0,1.05fr),minmax(0,0.95fr)] md:pl-4 ${
          isBottomCard ? "md:gap-6" : "md:gap-5"
        }`}
      >
        <div className="mb-4 md:mb-0">
          <div
            className={`${iconBoxClass("xl", item.styles.iconBox)}`}
          >
            <CardIcon className="size-6" strokeWidth={1.75} />
          </div>
        </div>
        <div>
          <span className="inline-block rounded-lg bg-slate-100/90 px-2.5 py-1 text-sm font-extrabold tabular-nums leading-none text-slate-800 ring-1 ring-slate-200/80">
            {item.number}
          </span>
          <h3 className="font-display mt-3 text-card-title font-bold tracking-tight text-slate-900">
            {item.title}
          </h3>
          <p className="mt-2 max-w-md text-base leading-relaxed text-slate-600">
            {item.description}
          </p>
        </div>
        <ul
          className={`mt-4 text-base leading-relaxed text-secondary ${
            item.points.length > 4
              ? "grid grid-cols-2 gap-x-4 gap-y-2.5"
              : "space-y-2.5"
          } ${isBottomCard ? "md:mt-8" : "md:mt-14"}`}
        >
          {item.points.map((point) => (
            <li key={point} className="flex gap-2.5">
              <Check
                className={`mt-0.5 size-4 shrink-0 ${item.styles.check}`}
                strokeWidth={2.5}
              />
              <span className="leading-snug">{point}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-5 w-full min-h-0 shrink-0 self-stretch md:mt-0 md:w-[40%]">
        {renderProductPreview(item)}
      </div>
    </article>
  );
}

function ProductCardList({ featureRows, renderProductPreview }) {
  return (
    <div
      aria-label="Product capabilities"
      className="product-stack-stage product-stack-stage--static relative flex flex-col gap-6 pb-8"
    >
      {featureRows.map((item) => (
        <ProductFeatureCard
          key={item.number}
          item={item}
          renderProductPreview={renderProductPreview}
        />
      ))}
    </div>
  );
}

export default function ProductOverview() {
  const featureRows = [
    {
      number: "01",
      title: "Patient & Clinical Management",
      description:
        "Comprehensive patient records and clinical workflows in one place.",
      points: [
        "Unified profiles",
        "Medical records",
        "Workflows",
        "OT",
        "Cathlab",
        "Endoscopy",
        "Radiology",
        "Cardiology",
        "Emergency",
      ],
      Icon: UserRound,
      styles: {
        iconBox: "bg-blue-100 text-blue-600",
        check: "text-blue-600",
        accentBar: "bg-blue-600",
      },
      previewImage: "images/patient-clinical-preview.svg",
      previewImageAlt:
        "Patient profile dashboard with overview navigation, visit timeline, and latest vitals",
      previewObjectFit: "cover",
    },
    {
      number: "02",
      title: "Appointments & Scheduling",
      description: "Smarter scheduling and reduced waiting times for patients.",
      points: ["Smart scheduling", "Availability tracking", "Queue management"],
      Icon: CalendarDays,
      styles: {
        iconBox: "bg-emerald-100 text-emerald-600",
        check: "text-emerald-600",
        accentBar: "bg-emerald-600",
      },
      previewImage: "images/appointments-scheduling-preview.svg",
      previewImageAlt: "Appointments and scheduling dashboard preview",
      previewObjectFit: "cover",
    },
    {
      number: "03",
      title: "Billing & Revenue Management",
      description: "Automate billing and manage claims without revenue leakage.",
      points: ["Automated billing", "Claim management", "Tracking dashboards"],
      Icon: CircleDollarSign,
      styles: {
        iconBox: "bg-amber-100 text-amber-600",
        check: "text-amber-600",
        accentBar: "bg-amber-500",
      },
      previewImage: "images/billing-revenue-preview.svg",
      previewImageAlt: "Billing and revenue management dashboard preview",
      previewObjectFit: "cover",
    },
    {
      number: "04",
      title: "Lab & Pharmacy Integration",
      description: "Connect orders, inventory, and reports with real-time sync.",
      points: ["Lab report management", "Inventory tracking", "Order workflows"],
      Icon: TestTube2,
      styles: {
        iconBox: "bg-violet-100 text-violet-600",
        check: "text-violet-600",
        accentBar: "bg-violet-600",
      },
      previewKind: "lab-pharmacy",
      previewImageAlt: "Lab and pharmacy integration workflow preview",
    },
    {
      number: "05",
      title: "Analytics & Reporting",
      description: "Make data-driven decisions with clear operational insights.",
      points: [
        "Real-time dashboards",
        "Operational insights",
        "Performance tracking",
      ],
      Icon: BarChart3,
      styles: {
        iconBox: "bg-accent-soft text-accent",
        check: "text-accent",
        accentBar: "bg-accent",
      },
      previewImage: "images/analytics-reporting-preview.svg",
      previewImageAlt: "Analytics and reporting dashboards preview",
    },
    {
      number: "06",
      title: "Integrations & Ecosystem",
      description: "Connect third-party systems and scale with your hospital.",
      points: [
        "Lab & insurance integrations",
        "API-ready architecture",
        "Data exchange",
        "PACS Integration",
      ],
      Icon: Plug2,
      styles: {
        iconBox: "bg-indigo-100 text-indigo-600",
        check: "text-indigo-600",
        accentBar: "bg-indigo-600",
      },
      previewKind: "integrations",
      previewImageAlt: "System integrations and connected services preview",
    },
  ];

  const renderProductPreview = (item) => (
    <div className={previewFrameClassName(item)}>
      {item.previewKind === "integrations" ? (
        <div
          className="h-full w-full min-h-0 p-1 md:p-1.5"
          role="img"
          aria-label={item.previewImageAlt}
        >
          <IntegrationsPreviewMock />
        </div>
      ) : item.previewKind === "lab-pharmacy" ? (
        <div
          className="h-full w-full min-h-0 p-1 md:p-1.5"
          role="img"
          aria-label={item.previewImageAlt}
        >
          <LabPharmacyPreviewMock />
        </div>
      ) : (
        <img
          src={`${process.env.PUBLIC_URL}/${item.previewImage}`}
          alt={item.previewImageAlt}
          className={previewImageClassName(item.previewObjectFit)}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );

  return (
    <section
      id="product"
      className="scroll-mt-24 relative min-h-dvh py-12 md:py-20 px-4 md:px-6"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-slate-50"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_55%_at_50%_-15%,rgba(37,99,235,0.14),transparent_55%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.07)_1px,transparent_1px)] bg-[length:44px_44px] [mask-image:radial-gradient(ellipse_75%_55%_at_50%_0%,#000_30%,transparent)] opacity-50"
        aria-hidden
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div
          className="product-stack-header sticky top-[5.5rem] z-20 -mx-4 px-4 pb-8 text-center max-lg:static max-lg:top-auto md:-mx-6 md:px-6 md:pb-10 isolate"
        >
          <div
            className="pointer-events-none absolute inset-0 bg-slate-50"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_55%_at_50%_-15%,rgba(37,99,235,0.14),transparent_55%)]"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.07)_1px,transparent_1px)] bg-[length:44px_44px] [mask-image:radial-gradient(ellipse_75%_55%_at_50%_0%,#000_30%,transparent)] opacity-50"
            aria-hidden
          />
          <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200/90 bg-white/90 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-blue-600 shadow-sm shadow-blue-900/5 backdrop-blur-sm">
            <Settings className="h-4 w-4" strokeWidth={2} aria-hidden />
            Product
          </span>
          <h2 className="mt-5 text-section font-bold leading-tight text-secondary max-w-4xl mx-auto [text-wrap:balance]">
            Everything You Need to Run a{" "}
            <span className="bg-gradient-to-r from-accent to-blue-500 bg-clip-text text-transparent">
              Modern Hospital
            </span>
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-base md:text-lg text-muted leading-relaxed">
            From patient care to operations and analytics—our platform covers
            every critical function in one unified system.
          </p>
          </div>
        </div>

        <ProductCardList
          featureRows={featureRows}
          renderProductPreview={renderProductPreview}
        />

        <motion.div
          variants={slowFadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="relative mt-12 md:mt-16 overflow-hidden rounded-3xl border border-slate-800/80 bg-slate-950 p-6 shadow-2xl shadow-slate-900/25 sm:p-8 md:p-10 lg:p-12"
        >
          <div
            className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-20"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/images/cta-trusted-providers-bg.jpg)`,
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%22160%22%20height%3D%22160%22%20viewBox%3D%220%200%20160%20160%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.09%22%3E%3Ccircle%20cx%3D%2212%22%20cy%3D%2212%22%20r%3D%221.2%22%2F%3E%3Ccircle%20cx%3D%2280%22%20cy%3D%2248%22%20r%3D%221%22%2F%3E%3Ccircle%20cx%3D%22140%22%20cy%3D%22108%22%20r%3D%221.2%22%2F%3E%3Ccircle%20cx%3D%2242%22%20cy%3D%22126%22%20r%3D%221%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] bg-[length:160px_160px] opacity-20"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -right-10 top-0 h-56 w-56 rounded-full bg-accent/30 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -left-16 bottom-0 h-48 w-48 rounded-full bg-indigo-500/25 blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_100%_0%,rgba(59,130,246,0.18),transparent_50%)]"
            aria-hidden
          />

          <div className="relative grid gap-8 lg:grid-cols-[1.15fr_minmax(0,1fr)] lg:items-center lg:gap-12">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-6">
              <div
                className={`flex h-16 w-16 shrink-0 items-center justify-center ${iconBoxShape} bg-gradient-to-br from-white/12 to-white/5 text-white backdrop-blur-sm`}
                aria-hidden
              >
                <Rocket className="size-8" strokeWidth={1.65} />
              </div>
              <div className="min-w-0 text-left text-white">
                <p className="text-pretty font-display text-2xl font-extrabold leading-[1.12] tracking-tight sm:text-3xl md:text-4xl">
                  Built for today.{" "}
                  <span className="text-slate-400">Ready for tomorrow.</span>
                </p>
                <p className="mt-4 text-lg font-semibold text-blue-100 sm:text-xl">
                  One platform, endless possibilities.
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-center rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm md:p-6 lg:border-l lg:border-t-0 lg:rounded-none lg:border-l-white/10 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
              <p className="text-base font-medium leading-relaxed text-slate-300">
                See how Gestalt HIS can transform your hospital operations.
              </p>
              <a
                href="#demo-form"
                className={`${primaryCtaClass} mt-5 w-full gap-2 px-6 py-3.5 text-base font-bold sm:w-auto`}
              >
                Schedule a Demo
                <ArrowRight className="size-5" aria-hidden />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}